import { useState, useEffect } from 'react';
import EcoButton from 'eco-design/base/eco-button';
import EcoImg from 'eco-design/base/eco-img';
import styles from './index.module.scss';
import getLocal from '@/app/utils/getLocal';
import { useLang } from '@/i18n';

import { getProductList } from '@/service/referral';
import type { IProductItem } from '@/components/Referral/index.d';
import { formatPrice } from '@/components/Referral/util';

interface ProductItem {
    id: string;
    image: string;
    name: string;
    specs: string;
    salePrice: string;
    originalPrice: string;
    discount: string;
    link: string;
}

export default function Purchase() {
    const { t } = useLang('Purchase');
    const { locale } = getLocal();
    const [productList, setProductList] = useState<ProductItem[]>([]);

    const specMap = {
        za: {
            42602939416785: '2–6kWh Capacity | 2400W Output | LiFePO4 Battery',
            42134700720337: '2kWh Capacity | 2400W Output | 3.1-hour Charge',
            42744383340753: '4kWh Capacity | 4000W Output | LiFePO4 Battery',
            41620506575057: '1kWh Capacity | 1800W Output | LiFePO4 Battery',
            42801700700369: '245Wh Capacity | 300W Output | <10 ms UPS ',
            41462253813969: '512Wh Capacity | 500W Output | LiFePO4 Battery',
            42841743655121: '15 Min Fast Cooling & Heating | 8-hour Runtime',
            41935487729873: '768Wh Capacity | 800W Output | LiFePO4 Battery',
        },
        kr: {
            46400158990505: '용량: 1kWh|출력: 1800W',
            46643436748969: '용량: 2kWh | 2400W 출력 |LFP인산철 배터리 | 30dB',
            46312076738729: '용량: 245Wh|출력: 300W',
            46470310822057: '용량: 572Wh|출력: 600W',
            46400200343721: '용량: 2kWh | 2400W 출력 |LFP인산철 배터리 | 30dB',
            45934989508777: '6,100 BTU 냉방 | 6,800 BTU 난방',
            43448625135785: '용량: 1kWh|출력: 1800W',
            46650397622441: '용량: 2kWh | 2400W 출력 |LFP인산철 배터리 | 30dB',
        },
        mx: {
            42614591291471: 'Capacidad de 1024Wh | Salida de 1800W',
            42614576775247: 'Capacidad de 268Wh | Salida de 600W',
            42614677340239: 'Capacidad de 4096Wh | Salida de 4000W',
            42614299983951: 'Capacidad de 230Wh | Salida de 300W',
            41939133923407: 'Capacidad de 2048Wh | Salida de 2400W',
            41939132022863: 'Capacidad de 3,6kWh | Salida de 3600W',
            41939130744911: 'Capacidad de 768Wh | Salida de 800W',
            42732210257999: 'Capacidad de 554Wh | Salida de 600W',
        },
        ph: {
            52064529219894: '1-5kWh Capacity | 1800W Output',
            51677738074422: 'UPS<10 ms | 858Wh Capacity | 600W Output',
            49116498067766: '768Wh Capacity | 800W Output',
            51917471711542: '2kWh-6kWh Capacity | 2400W Output｜LFP｜30dB ',
            51443622478134: '3.6kWh-25kWh Capacity | 3600W Output',
            52064579518774: '1-5kWh Capacity | 1800W Output',
            51894359589174: '768Wh Capacity | 4.5 hr the Fastest Charging Speed',
            51685758140726: 'UPS<10 ms | 858Wh Capacity | Fast charging in 6.4hrs',
        },
    };

    useEffect(() => {
        getProductList({ bizModule: 'REFERRAL', module: 'HOT' }, locale).then((res) => {
            if (res.code !== '0') return;
            const mappedProducts: ProductItem[] = res.data.map((item: IProductItem) => {
                const price = parseFloat(item.price);
                const compareAtPrice = parseFloat(item.compareAtPrice);
                let discount = '';
                if (compareAtPrice > price) {
                    discount = `${Math.round((1 - price / compareAtPrice) * 100)}%`;
                }
                return {
                    id: item.variantId || item.productId,
                    image: item.imageUrl,
                    name: item.title,
                    specs: specMap[locale]?.[item.variantId] || '',
                    salePrice: formatPrice(locale, price),
                    originalPrice: compareAtPrice > price ? formatPrice(locale, compareAtPrice) : '',
                    discount,
                    link: item.productUrl,
                };
            });
            setProductList(mappedProducts);
        });
    }, [locale]);
    function handleBuyNow(product: ProductItem) {
        window.open(product.link, '_blank');
    }

    if (productList.length === 0) return null;

    return (
        <div className={`${styles.Purchase} GtmModuleData-section`}>
            <div className="sh-layout">
                <div className="purchase-header">
                    <h2 className="purchase-title">{t('title')}</h2>
                    <h2 className="purchase-desc">{t('desc')}</h2>
                </div>

                <div className="product-grid">
                    {productList.map((product) => (
                        <div className="product-card" key={product.id}>
                            {product.discount && (
                                <div className="discount-tag">
                                    <span>{product.discount}</span>
                                    <span className="off-text">{t('off')}</span>
                                </div>
                            )}
                            <div className="product-image">
                                <EcoImg pc={product.image} />
                            </div>
                            <div className="product-info">
                                <h3 className="product-name">{product.name}</h3>
                                <p className="product-specs">{product.specs}</p>
                                <div>
                                    <div className="product-price">
                                        <span className="sale-price">{product.salePrice}</span>
                                        <span className="original-price">{product.originalPrice}</span>
                                    </div>
                                    <div className="product-action">
                                        <EcoButton lRadius="ellipse" lTheme="black" onClick={() => handleBuyNow(product)}>
                                            {t('buy_now')}
                                        </EcoButton>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
